/**
 * Shared Conscious alerts SSE — one EventSource per tenant per session
 */

import { config } from '../config/environment';
import type { ConsciousAlert } from './consciousApi';

export type ConsciousAlertsSsePayload = {
  count?: number;
  alerts?: ConsciousAlert[];
};

type AlertListener = (payload: ConsciousAlertsSsePayload) => void;
type ConnectionListener = (connected: boolean) => void;

interface TenantConnection {
  source: EventSource;
  alertListeners: Set<AlertListener>;
  connectionListeners: Set<ConnectionListener>;
  reconnectTimer: ReturnType<typeof setTimeout> | undefined;
}

const connections = new Map<string, TenantConnection>();

const baseUrl = () => config.api.baseUrl;

const RECONNECT_DELAY_MS = 3_000;

function hasSubscribers(conn: TenantConnection): boolean {
  return conn.alertListeners.size > 0 || conn.connectionListeners.size > 0;
}

function scheduleReconnect(tenantId: string): void {
  const existing = connections.get(tenantId);
  if (existing?.reconnectTimer !== undefined) return;
  if (!existing || !hasSubscribers(existing)) return;

  existing.reconnectTimer = setTimeout(() => {
    const conn = connections.get(tenantId);
    if (!conn || !hasSubscribers(conn)) return;
    conn.reconnectTimer = undefined;
    openConnection(tenantId);
  }, RECONNECT_DELAY_MS);
}

function openConnection(tenantId: string): TenantConnection | null {
  if (typeof EventSource === 'undefined') return null;

  const prior = connections.get(tenantId);
  if (prior) {
    prior.source.close();
    if (prior.reconnectTimer !== undefined) {
      clearTimeout(prior.reconnectTimer);
    }
  }

  const url = new URL(`${baseUrl()}/api/conscious/alerts/stream`);
  url.searchParams.set('tenantId', tenantId);
  const source = new EventSource(url.toString());

  const conn: TenantConnection = {
    source,
    alertListeners: prior?.alertListeners ?? new Set(),
    connectionListeners: prior?.connectionListeners ?? new Set(),
    reconnectTimer: undefined,
  };
  connections.set(tenantId, conn);

  const notifyConnected = () => {
    conn.connectionListeners.forEach((listener) => listener(true));
  };

  const onAlerts = (event: MessageEvent<string>) => {
    try {
      const payload = JSON.parse(event.data) as ConsciousAlertsSsePayload;
      conn.alertListeners.forEach((listener) => listener(payload));
    } catch {
      conn.alertListeners.forEach((listener) => listener({}));
    }
  };

  source.addEventListener('connected', notifyConnected as EventListener);
  source.addEventListener('alerts', onAlerts as EventListener);
  source.onopen = notifyConnected;
  source.onerror = () => {
    conn.connectionListeners.forEach((listener) => listener(false));
    source.close();
    if (hasSubscribers(conn)) {
      connections.delete(tenantId);
      scheduleReconnect(tenantId);
    } else {
      connections.delete(tenantId);
    }
  };

  return conn;
}

function ensureConnection(tenantId: string): TenantConnection | null {
  const existing = connections.get(tenantId);
  if (existing && existing.source.readyState !== EventSource.CLOSED) {
    return existing;
  }
  return openConnection(tenantId);
}

function maybeCloseConnection(tenantId: string): void {
  const conn = connections.get(tenantId);
  if (!conn || hasSubscribers(conn)) return;
  if (conn.reconnectTimer !== undefined) {
    clearTimeout(conn.reconnectTimer);
  }
  conn.source.close();
  connections.delete(tenantId);
}

export function subscribeConsciousAlertsSse(
  tenantId: string,
  onAlerts: AlertListener,
  onConnection?: ConnectionListener
): () => void {
  const conn = ensureConnection(tenantId);
  if (!conn) return () => {};

  conn.alertListeners.add(onAlerts);
  if (onConnection) conn.connectionListeners.add(onConnection);

  return () => {
    conn.alertListeners.delete(onAlerts);
    if (onConnection) conn.connectionListeners.delete(onConnection);
    maybeCloseConnection(tenantId);
  };
}

/** @internal test-only */
export function __resetConsciousAlertsSseClientForTests(): void {
  for (const conn of connections.values()) {
    if (conn.reconnectTimer !== undefined) clearTimeout(conn.reconnectTimer);
    conn.source.close();
  }
  connections.clear();
}

/** @internal test-only */
export function __getConsciousAlertsSseActiveConnectionCount(): number {
  return connections.size;
}
